/**
 * The HTTP client.
 *
 * Two things it does that a bare `fetch` does not:
 *
 * 1. Unwraps the API's `{"error": {"code", "message"}}` envelope into a typed
 *    error, so a screen can branch on `quota_exceeded` rather than on prose.
 * 2. Refreshes a stale access token **once** and retries, so a receptionist
 *    mid-shift never sees a login screen because thirty minutes elapsed.
 */

import { clearSession, getAccessToken, getActingClinic, getRefreshToken, storeSession } from "./session";
import type { TokenPair } from "./types";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Raised when there is no usable session left; the caller shows the login screen. */
export class NotAuthenticatedError extends ApiError {
  constructor() {
    super(401, "not_authenticated", "Please sign in again.");
    this.name = "NotAuthenticatedError";
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Public endpoints — the patient tracker — must not trigger a refresh. */
  anonymous?: boolean;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (response.status === 401 && !options.anonymous) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) throw new NotAuthenticatedError();
    return unwrap<T>(await send(path, options));
  }
  return unwrap<T>(response);
}

/**
 * The bearer header, for the callers that cannot go through `api()`.
 *
 * Two of them: the stream, which reads a response body rather than JSON, and
 * the CSV export, which wants a Blob. Both still need the credential, and a
 * bare `<a href>` would arrive without one and come back as a login page saved
 * under a `.csv` name.
 */
export function authorised(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function send(path: string, options: RequestOptions): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (!options.anonymous) Object.assign(headers, authorised());

  return fetch(`${API_BASE}${path}`, {
    method: options.method ?? "GET",
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });
}

async function unwrap<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  if (response.ok) return (await response.json()) as T;

  const body: unknown = await response.json().catch(() => null);
  const error = extractError(body);
  if (response.status === 401) throw new NotAuthenticatedError();
  throw new ApiError(response.status, error.code, error.message);
}

function extractError(body: unknown): { code: string; message: string } {
  if (body && typeof body === "object" && "error" in body) {
    const { error: envelope } = body;
    if (envelope && typeof envelope === "object") {
      const { code, message } = envelope as { code?: string; message?: string };
      return { code: code ?? "unknown", message: message ?? "Something went wrong." };
    }
  }
  return { code: "unknown", message: "Something went wrong." };
}

/**
 * Exchange the refresh token for a new pair.
 *
 * Concurrent callers share one in-flight request. Without that, a console that
 * fires four requests as a token expires would rotate four times — and the
 * backend treats a re-presented refresh token as theft and logs the user out
 * of everything.
 */
let inFlightRefresh: Promise<boolean> | null = null;

export function refreshAccessToken(): Promise<boolean> {
  inFlightRefresh ??= doRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function doRefresh(): Promise<boolean> {
  const refresh = getRefreshToken();
  if (!refresh) return false;

  // A superadmin's chosen clinic lives only in this access token, so it would
  // otherwise vanish the moment a thirty-minute token expires mid-shift. Every
  // silent refresh re-asks for the same one, which is what keeps "acting"
  // feeling like a standing choice rather than something that quietly times out.
  const acting = getActingClinic();
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      refresh_token: refresh,
      ...(acting ? { acting_clinic_id: acting.id } : {}),
    }),
  });
  if (!response.ok) {
    clearSession();
    return false;
  }

  const pair = (await response.json()) as TokenPair;
  storeSession(pair.access_token, pair.refresh_token);
  return true;
}
